import { type AxiosError, type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'

import { canUseWindow } from './utils/canUse'
import { type AuthenticationSdk } from './authentication'
import { type Configuration, type OutputErrorMessage } from './generated'
import { FORBIDDEN_STATUS_CODE, TOO_MANY_REQUEST_STATUS_CODE } from './interceptors.constants'
import { type AugmentedAxiosRequestConfig } from './interceptors.types'
import { type UserSdk } from './user'

const isAugmentedAxiosRequestConfig = (config: unknown): config is AugmentedAxiosRequestConfig =>
  (config as AugmentedAxiosRequestConfig).sibr !== undefined

const createRefreshTokensIfNeededRequestInterceptor =
  (userSdk: UserSdk, authenticationSdk: AuthenticationSdk) =>
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    if (isAugmentedAxiosRequestConfig(config) && config.sibr.shouldRefreshTokens && userSdk.isRefreshTokensNeeded()) {
      await authenticationSdk.refreshTokensFromInterceptor()
    }

    return config
  }

const passThroughError = (error: unknown): Promise<unknown> => Promise.reject(error)

export const resetTokenWhenErrorIsTypeOfInvalidJwt =
  (userSdk: UserSdk) =>
  ({ isAxiosError, response }: AxiosError<OutputErrorMessage>): void => {
    if (isAxiosError && response?.status === 401 && response?.data?.type === '/authentication/invalid-jwt-token') {
      userSdk.setInvalidTokenData()
    }
  }

export const addAxiosRequestInterceptors = (
  axios: AxiosInstance,
  configuration: Configuration,
  userSdk: UserSdk,
  authenticationSdk: AuthenticationSdk,
): void => {
  const refreshTokensIfNeededRequestInterceptor = createRefreshTokensIfNeededRequestInterceptor(
    userSdk,
    authenticationSdk,
  )

  // Front end url is deduced from basePath in sdk configuration
  const frontEndUrl = configuration?.basePath?.replace(/\/bff$/, '')
  axios.interceptors.request.use(refreshTokensIfNeededRequestInterceptor, passThroughError)

  axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError<OutputErrorMessage>) => {
      if (
        (error.response?.status === FORBIDDEN_STATUS_CODE || error.response?.status === TOO_MANY_REQUEST_STATUS_CODE) &&
        error.response?.headers['cf-mitigated'] === 'challenge' &&
        canUseWindow()
      ) {
        window.location.href = `${frontEndUrl || ''}/challenge?resumeUrl=${encodeURIComponent(window.location.href)}`

        return new Promise(() => {}) // Keep the promise pending to avoid further processing while redirecting
      }

      return Promise.reject(error)
    },
  )

  axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError<OutputErrorMessage>) => {
      resetTokenWhenErrorIsTypeOfInvalidJwt(userSdk)(error)

      throw error
    },
  )
}
